import gsap from 'gsap';
import { SplitText } from 'gsap/SplitText.js';
import { lisaUserData } from '../../modules/Lisa'
import { copyToClipboard } from "../../utils/html"
import { getValidations } from '../LisaValidations';
import { useVuelidate } from '@vuelidate/core'

gsap.registerPlugin(SplitText);

export default {
    setup: () => ({ v$: useVuelidate() }),
    delimiters: ['{(', ')}'],
    props: {
        step: {
            type: Object,
            required: true
        },
        stepSlug: {
            type: String,
            required: true
        },
        showStepContent: {
            type: Boolean,
            required: true
        },
        muted: {
            type: Boolean,
            required: true
        },
        locale: {
            type: String,
            required: true
        },
        forceSentence: {
            type: [Object, null],
            required: true
        },
        previousSentence: {
            type: [Object, null],
            required: true
        },
        animateDialog: {
            type: Boolean,
            required: true
        },
        assetsVersion: {
            type: String,
            required: true
        },
        canGoBack: {
            type: Boolean,
            default: true
        },
        errors: {
            type: Array,
            required: false
        }
    },
    data() {
        return {
            lisaUserData,
            sentenceSlug: null,
        }
    },
    validations() {
        return getValidations(this.stepSlug, this.locale)
    },
    beforeMount() {
        if(this.step.note) this.step.note.active = false
    },
    mounted() {
        if(
            this.step.modelUpdate
            && this.step.modelUpdate.key
        ) {
            if(this.step.modelUpdate.value) lisaUserData[this.step.modelUpdate.key] = this.step.modelUpdate.value;
            else delete lisaUserData[this.step.modelUpdate.key];
        }
    },
    computed:{
        sentence() {
            if(this.forceSentence) {
                this.sentenceSlug = this.forceSentence.slug;
                return this.forceSentence.value;
            }

            if(this.step.dialog.key) { // KEY MODE
                const { key, keyMode, list } = this.step.dialog;

                if(keyMode == 'random') {
                    const dialogSlugs = Object.keys(list[lisaUserData[key]])
                    const pickedDialog = dialogSlugs[Math.floor(dialogSlugs.length * Math.random())]
                    this.sentenceSlug = pickedDialog;

                    return list[lisaUserData[key]][pickedDialog];
                } else if(keyMode == 'concat') {
                    const dialogSlugs = Object.keys(list)
                    const pickedDialog = dialogSlugs[Math.floor(dialogSlugs.length * Math.random())]
                    const concatDialog = pickedDialog+'.'+lisaUserData[key]
                    this.sentenceSlug = concatDialog;

                    return list[pickedDialog][concatDialog]
                } else {
                    return "Error: Unknown keyMode"
                }
            } else if(this.step.dialog.list) { // DEFAULT MODE
                let { list } = this.step.dialog;
                if(this.lisaUserData['special'] && this.step.dialog[this.lisaUserData['special']]) list = this.step.dialog[this.lisaUserData['special']];

                const dialogSlugs = Object.keys(list);
                const pickedDialog = dialogSlugs[Math.floor(dialogSlugs.length * Math.random())]
                this.sentenceSlug = pickedDialog;

                return list[pickedDialog];
            } else { // ERROR
                console.warn('No dialog list given')
                console.log(this.step.dialog);
            }
        },
        slotGiven() {
            return this.$slots.default;
        },
        availableChoices() {
            if(!this.step.choices) return false;

            if(this.step.choices.key) {
                return this.step.choices.value[lisaUserData[this.step.choices.key]]
            } else {
                return this.step.choices.filter(item => !item.hide);
            }
        }
    },
    methods: {
        skip() {
            this.$refs.dialog.skip();
        },
        next() {
            this.$emit('next')
        },
        back() {
            this.$emit('back')
        },
        async submit(e) {
            e.preventDefault();
            e.stopPropagation();

            const result = await this.v$.$validate()
            if (!result) {
                // notify user form is invalid
                for(const error of this.v$.$errors) {
                    gtag?.('event', 'flow_field_error', {
                        flow: this.lisaUserData.goal,
                        step: this.stepSlug,
                        field: error.$property,
                        error: error.$validator,
                        message: error.$message
                    });
                }
                return
            }
            this.next();
        },
        choose(e) {
            const el = e.target;

            const { key, value, target, href, emit } = el.dataset
            const label = el.innerText;
            if(key && value) {
                lisaUserData[key] = value;
            } else if(key && !value) {
                delete lisaUserData[key];
            }

            if(key == 'goal') {
                gtag?.('event', 'flow_start', {
                    flow: lisaUserData.goal,
                    audio: this.muted ? 'muted' : 'unmuted'
                });
            }

            if(target) this.$emit('goToStep', target)
            if(href) this.$emit('navigateTo', href)
            if(emit) this.$emit(emit);
        },
        showVisual(e) {
            const { target } = e;
            if(target.dataset.mediaHover) {
                this.$emit('setContent', [target.dataset.mediaHover, 'gif'])
            }
        },
        hideVisual(e) {
            const { target } = e;
            if(target.dataset.mediaHover) {
                this.$emit('idleReset')
            }
        },
        completeIntro() {
            this.$emit('stepIntroUpdate', true)
        },
        copyToClipboard(e) {
            e.preventDefault();
            e.stopPropagation();

            copyToClipboard(e.currentTarget)
        },
        onProcessFile(error, file, target) {
            if(!lisaUserData[target]) lisaUserData[target] = [];
            lisaUserData[target].push(file.serverId);
        },
        onBeforeRemoveFile(file, target) {
            // Don't do anything if the field is not set or is set to false
            if(!lisaUserData[target] && lisaUserData[target] !== false) return;
            if(!Array.isArray(lisaUserData[target])) { // Remove the field if it's not an array
                delete lisaUserData[target];
                return;
            }

            // Remove this specific file from the array
            const index = lisaUserData[target].indexOf(file.serverId)
            if(index >= 0) {
                lisaUserData[target].splice(index, 1);
            }
        },
        onFileError(error, target) {
            // Set the field to false to trigger the validation error
            lisaUserData[target] = false;
        },
        onCheckboxesChange(e, target, value) {
            if(!lisaUserData[target]) lisaUserData[target] = [];

            const valueIndex = lisaUserData[target].indexOf(value);
            if(valueIndex >= 0) {
                lisaUserData[target].splice(valueIndex, 1);
            } else {
                lisaUserData[target].push(value);
            }

            if(!lisaUserData[target].length) delete lisaUserData[target];
        },
        onMonthClick(e) {
            const { target } = e;
            target.showPicker?.();
        },
        isHrefExternal(value) {
            let url;
            try {
                const anchor = document.createElement('a')
                anchor.href = value;
                url = new URL(anchor.href)
                return (window.location.origin != url.origin && !value.startsWith('mailto:') && !value.startsWith('tel:')) || value.startsWith('mailto:')
            } catch (error) {
                console.error(error)
                return true;
            }
        }
    },
    watch: {
        sentenceSlug() {
            this.$nextTick(() => {
                if(!this.muted)
                    this.$emit('playSpeech', '/assets/lisa/'+this.locale+'/'+this.sentenceSlug+'.mp3?v='+this.assetsVersion);

                this.$emit('stepIntroUpdate', false)
                this.$emit('historyPush', { slug: this.stepSlug, sentence: { value: this.sentence, slug: this.sentenceSlug}});
            })
        },
        previousSentence() {
            this.$nextTick(() => {
                new SplitText(this.$refs.previous, { type: 'chars, words', tag: 'div' });
            })
        }
    }
}
